import { useCallback, useEffect } from 'react'
import Add from '@mui/icons-material/Add'
import { Box, Button, Paper } from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import useStatefulSearchParam from '@/hooks/useStatefulSearchParam'
import {
  useGetProjectDocumentRules,
  useUpdateProjectDocumentRule,
} from '@/service-library/hooks/project-document-rules'
import { useGetProjectGridFields } from '@/service-library/hooks/project-grid-fields'
import queryKeys from '@/service-library/query-keys'
import { getFieldsFromProject } from '@/utils/field'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import RuleSettings, { FormValues } from '@/components/rules/RuleSettings'
import SettingsPage from '@/components/shared/settings/SettingsPage'
import DocumentRules from './DocumentRules'

export default function DocumentRulesPage() {
  const { project } = useProjectContext()
  const allFields = getFieldsFromProject(project)

  const documentRulesQuery = useGetProjectDocumentRules({
    filters: {
      project_id: project.id,
      fields__include: 'type,workflow_states_ids,dependent_fields_ids',
      limit: '1000',
    },
    enabled: !!project,
  })

  const { projectGridFields } = useGetProjectGridFields({
    filters: {
      limit: '1000',
      fields__only: 'id,name,project_grid_field_type,full_name',
      project_id: project.id,
    },
    initialListData: allFields,
    staleTime: 1000,
    enabled: !!project,
  })

  const addRuleOverlay = useOverlay()

  const {
    value: selectedRuleId,
    isInUrl,
    toggleValue,
    resetValue,
  } = useStatefulSearchParam({
    param: 'rule_id',
  })

  const { updateProjectDocumentRuleAsync, isPending } =
    useUpdateProjectDocumentRule({
      sideEffectQueryKeys: [queryKeys.projectDocumentRules.details()],
      onSuccess: () => {
        showSuccessSnackbar('Document Rule Updated')
      },
      onError: () => {
        showErrorSnackbar('Document Rule Update Failed')
      },
    })

  const { projectDocumentRules, isPending: rulesIsLoading } = documentRulesQuery
  const selectedRule = projectDocumentRules.find(
    (rule) => rule.id === selectedRuleId,
  )

  const defaultRuleId = projectDocumentRules[0]?.id

  const setSelectedRuleId = useCallback(
    (ruleId: string | undefined) => {
      if (ruleId) {
        toggleValue(ruleId)
        return
      }

      if (defaultRuleId) {
        toggleValue(defaultRuleId)
      } else if (isInUrl) {
        resetValue()
      }
    },
    [defaultRuleId, isInUrl, resetValue, toggleValue],
  )

  const updateDocumentRule = async ({
    workflow_states_ids,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    run_in_all_workflow_states, // Is passed in the form values for document rules but is not actually a supported parameter, so we ignore it here
    dependent_fields,
    is_superuser_rule,
    ...rest
  }: FormValues) => {
    if (selectedRule) {
      return updateProjectDocumentRuleAsync({
        ...selectedRule,
        dependent_fields_ids: dependent_fields.map(({ id }) => id),
        workflow_states_ids,
        is_superuser_rule,
        params: rest,
      })
    }
    return
  }

  useEffect(() => {
    if (!rulesIsLoading && !selectedRule) {
      setSelectedRuleId(undefined)
    }
  }, [selectedRuleId, selectedRule, setSelectedRuleId, rulesIsLoading])

  return (
    <>
      <ProjectBreadcrumb label="Document Rules" url="../document-rules" />

      <SettingsPage
        title="Document Rules"
        fill
        description="Rules that check whole documents. They run in order from top to bottom, so drag a rule to change when it runs."
        width="lg"
        actions={
          <Button
            variant="text"
            startIcon={<Add />}
            onClick={addRuleOverlay.open}
            disabled={rulesIsLoading}
          >
            Add rule
          </Button>
        }
      >
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            minHeight: 480,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', p: 2 }}>
            <DocumentRules
              documentRulesQuery={documentRulesQuery}
              selectedRuleId={selectedRuleId}
              setSelectedRuleId={setSelectedRuleId}
              addRuleOverlay={addRuleOverlay}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: 1,
              borderColor: 'divider',
            }}
          >
            {selectedRule && (
              <RuleSettings
                key={selectedRule.id} // Completely rerender each time we change the selected rule
                isLoading={isPending}
                onSubmit={updateDocumentRule}
                rule={selectedRule}
                fields={projectGridFields}
              />
            )}
          </Box>
        </Paper>
      </SettingsPage>
    </>
  )
}
